import { Resource, ResourceType } from './Resource';
import {
    __BLUEBELL_UMD_EXPORT_RESOLVER__,
    __BLUEBELL_GLOBAL_MAP_UMD_EXPORTED__,
    BLUEBELL_UMD_EXPORT_RESOLVER_VERSION,
} from './constant';

import { noteGlobalProps, getGlobalProp } from './utils/global-prop';
import { createGlobalFactory } from './utils/createGlobalFactory';
import { promiseIgnoreCatch } from './utils/promiseIgnoreCatch';
import { smellsLikeAPromise } from './utils/smellsLikeAPromise';
import { murmurhash3_32_gc } from './utils/murmurhash3_gc';
import { ensureGlobalMap } from './utils/ensureGlobalMap';
import { Debugger } from './utils/Debugger';

/**
 * Resolve the exported object from a UMD-formatted JS resource.
 */
class UmdExportResolver extends Debugger {
    static #instance: UmdExportResolver;

    readonly #exportedMap = ensureGlobalMap<string, keyof Window>(__BLUEBELL_GLOBAL_MAP_UMD_EXPORTED__);

    #currentResolvingUrl: string | null = null;

    #resolvingPromise: Promise<keyof Window> | null = null;

    private constructor() {
        super();
        /* istanbul ignore if: difficult to enter */
        if (UmdExportResolver.#instance) {
            throw Error('UmdExportResolver cannot be created more than once.');
        }
    }

    protected get debugName(): string {
        return 'umd-export-resolver';
    }

    public get version(): number {
        return BLUEBELL_UMD_EXPORT_RESOLVER_VERSION;
    }

    public static getInstance(): UmdExportResolver {
        if (!UmdExportResolver.#instance) {
            UmdExportResolver.#instance = new UmdExportResolver();
        }

        return UmdExportResolver.#instance;
    }

    /**
     * Resolve exported object from a UMD resource.
     * @param res A UMD resource.
     * @param ref A reference which the resource belongs to.
     * @returns Promise of the exported object resoved.
     */
    public resolveResource<T>(res: Resource, ref: unknown): Promise<T> {
        this.debug('Call resolveResource(%O, %O).', res, ref);

        if (res.type !== ResourceType.JS) {
            return Promise.reject(Error(`${res} cannot be resolved for UMD exported.`));
        }

        return this.resolve(() => res.ref(ref), res.src).then(s => window[s]);
    }

    /**
     * Resolve exported object from an eval function.
     *
     * If the function returns a promise, wait for it to finish up.
     * @param evalScript Eval function to inject global key.
     * @param src Source url of script, as key of cache.
     * @returns Promise of the resolved global key.
     */
    public resolve(evalScript: () => object | undefined, src: string): Promise<keyof Window> {
        this.debug('Call resolve(%o, %s)', evalScript, src);

        src = src.trim();

        if (this.#resolvingPromise) {
            if (src && src === this.#currentResolvingUrl) {
                this.debug('Reuse the existing #resolvingPromise.');
                return this.#resolvingPromise;
            }

            this.debug('Wait for the existing #resolvingPromise to finish up.');
            return promiseIgnoreCatch(this.#resolvingPromise).then(() => this.resolve(evalScript, src));
        }

        this.debug("Let's create #resolvingPromise.");

        this.#currentResolvingUrl = src;

        this.#resolvingPromise = Promise.resolve()
            .then(async () => {
                noteGlobalProps();

                const ret = evalScript();

                if (ret && smellsLikeAPromise(ret)) {
                    this.debug("It's an async function, wait for it to finish up.");
                    await ret;
                }

                let entryName = getGlobalProp() as keyof Window;

                let srcKey = '';
                let cachedKey: keyof Window | undefined = undefined;

                if (src) {
                    const start = performance.now();
                    // 10ms for 400kb text
                    srcKey = String(murmurhash3_32_gc(src, 0x0810));

                    const costs = performance.now() - start;

                    if (costs > 10) {
                        console.warn(
                            `hash(${
                                src.length > 50 ? `${src.slice(0, 50)}...` : src
                            }) costs more than 10ms, avoid using big text as src.`
                        );
                    }

                    if (this.debug.enabled) {
                        this.debug('hash(%s) costs %sms.', src.length > 50 ? `${src.slice(0, 50)}...` : src, costs);
                    }

                    cachedKey = this.#exportedMap.get(srcKey);
                }

                if (!entryName) {
                    if (cachedKey) {
                        this.debug('Use cached key %s for %s.', cachedKey, src);
                        entryName = cachedKey;
                    } else {
                        throw Error(`Cannot find UMD exported name in ${src}.`);
                    }
                }

                if (cachedKey && cachedKey !== entryName) {
                    console.warn(
                        `The resolved UMD exported key "${entryName}" does not equal with previously cached "${cachedKey}" from "${src}".`
                    );
                }

                if (!window[entryName]) {
                    throw Error(`Cannot find UMD exported object in ${src}.`);
                }

                if (srcKey) {
                    this.#exportedMap.set(srcKey, entryName);
                }

                return entryName;
            })
            .finally(() => {
                this.#currentResolvingUrl = null;
                this.#resolvingPromise = null;
            });

        return this.#resolvingPromise;
    }

    public get [Symbol.toStringTag](): string {
        return 'UmdExportResolver';
    }
}

export type { UmdExportResolver };

export const getUmdExportResolver = createGlobalFactory<UmdExportResolver>(
    __BLUEBELL_UMD_EXPORT_RESOLVER__,
    () => UmdExportResolver.getInstance(),
    BLUEBELL_UMD_EXPORT_RESOLVER_VERSION,
    // Conflict is better than throwing error.
    true
);
